import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Grid,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Warning, CheckCircle, Phone, LocationOn } from '@mui/icons-material';
import apiService from '../services/apiService';

const formatAlertTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

const EmergencyPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = Active, 1 = Resolved

  const fetchEmergencyAlerts = useCallback(async (status) => {
    try {
      setLoading(true);
      setError('');
      const filterStatus = status !== undefined ? status : (activeTab === 0 ? 'active' : 'resolved');
      const response = await apiService.getEmergencyAlerts({ status: filterStatus });

      if (response.success) {
        setAlerts(response.data);
      } else {
        setError(response.message || 'Failed to load emergency alerts');
      }
    } catch (err) {
      console.error('Emergency alerts error:', err);
      setError('Failed to load emergency alerts');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchEmergencyAlerts();

    // Setup Socket.io listener for real-time alerts
    const socketService = require('../services/socketService').default;
    socketService.onEmergencyAlert((alert) => {
      // Only add to list if viewing active tab
      if (activeTab === 0) {
        setAlerts((prev) => [alert, ...prev]);
      }
      // Play alert sound regardless of tab
      const audio = new Audio('/alert.mp3');
      audio.play().catch(e => console.log('Audio play failed:', e));
    });
  }, [fetchEmergencyAlerts, activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const status = newValue === 0 ? 'active' : 'resolved';
    fetchEmergencyAlerts(status);
  };

  const handleView = (alert) => {
    setSelectedAlert(alert);
    setResolveNotes('');
    setOpenDialog(true);
  };

  const handleResolve = async () => {
    if (!selectedAlert) return;

    if (!resolveNotes) {
      alert('Please provide resolution notes');
      return;
    }

    if (!window.confirm('Mark this emergency as resolved?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiService.resolveEmergencyAlert(selectedAlert.id, {
        notes: resolveNotes,
      });

      if (response.success) {
        alert('Emergency marked as resolved');
        setOpenDialog(false);
        fetchEmergencyAlerts();
      } else {
        alert(response.message || 'Failed to resolve emergency');
      }
    } catch (err) {
      alert('Failed to resolve emergency');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: 'error', icon: <Warning /> },
      resolved: { label: 'Resolved', color: 'success', icon: <CheckCircle /> },
    };

    const config = statusConfig[status] || statusConfig.active;
    return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'Alert ID',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="error.main">
          {params.row.id}
        </Typography>
      ),
    },
    {
      field: 'crn',
      headerName: 'CRN',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
          {params.row.crn || params.row.rideId?.substring(0, 8) + '...'}
        </Typography>
      ),
    },
    {
      field: 'passengerName',
      headerName: 'Passenger',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.passengerName || params.row.userName || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.passengerPhone || params.row.userPhone || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'driverName',
      headerName: 'Driver',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.driverName || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.driverPhone || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.location || params.row.pickup_address || `${params.row.location_lat || ''}, ${params.row.location_lng || ''}`}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => getStatusChip(params.row.status),
    },
    {
      field: 'createdAt',
      headerName: 'Time',
      width: 180,
      renderCell: (params) => {
        const dateStr = params.row.createdAt || params.row.created_at;
        return formatAlertTime(dateStr);
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleView(params.row)}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" href={`tel:${params.row.passengerPhone}`}>
            <Phone fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="error.main">
            Emergency Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Handle emergency SOS alerts from passengers
          </Typography>
        </Box>
        <Chip
          icon={<Warning />}
          label={`${alerts.filter(a => a.status === 'active').length} Active`}
          color="error"
          sx={{ fontSize: '1rem', py: 2.5 }}
        />
      </Box>

      {/* Tabs for Active / Resolved */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            label="Active Alerts"
            icon={<Warning />}
            iconPosition="start"
            sx={{ color: activeTab === 0 ? 'error.main' : 'text.secondary' }}
          />
          <Tab
            label="Resolved Alerts"
            icon={<CheckCircle />}
            iconPosition="start"
            sx={{ color: activeTab === 1 ? 'success.main' : 'text.secondary' }}
          />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && alerts.length === 0 && (
        <Alert severity={activeTab === 0 ? 'success' : 'info'} sx={{ mb: 3 }}>
          {activeTab === 0 ? 'No active emergency alerts' : 'No resolved emergency alerts'}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={alerts}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* View Emergency Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="h6">Emergency Alert Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {selectedAlert.status === 'active' ? (
                    <Alert severity="error">
                      <strong>ACTIVE EMERGENCY - Immediate Action Required</strong>
                    </Alert>
                  ) : (
                    <Alert severity="success">
                      <strong>RESOLVED</strong> - This emergency has been resolved
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Alert ID</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedAlert.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">CRN</Typography>
                  <Typography variant="body1" fontFamily="monospace">{selectedAlert.crn || selectedAlert.rideId || selectedAlert.ride_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Passenger</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedAlert.passengerName || selectedAlert.userName}</Typography>
                  <Button
                    size="small"
                    startIcon={<Phone />}
                    href={`tel:${selectedAlert.passengerPhone || selectedAlert.userPhone}`}
                    sx={{ mt: 1 }}
                  >
                    {selectedAlert.passengerPhone || selectedAlert.userPhone}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Driver</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedAlert.driverName || 'N/A'}</Typography>
                  {selectedAlert.driverPhone && (
                    <Button
                      size="small"
                      startIcon={<Phone />}
                      href={`tel:${selectedAlert.driverPhone}`}
                      sx={{ mt: 1 }}
                    >
                      {selectedAlert.driverPhone}
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Location</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedAlert.location || selectedAlert.pickup_address || `${selectedAlert.location_lat}, ${selectedAlert.location_lng}`}</Typography>
                  <Button
                    size="small"
                    startIcon={<LocationOn />}
                    href={`https://www.google.com/maps?q=${selectedAlert.latitude || selectedAlert.location_lat},${selectedAlert.longitude || selectedAlert.location_lng}`}
                    target="_blank"
                    sx={{ mt: 1 }}
                  >
                    View on Map
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Alert Time</Typography>
                  <Typography variant="body1">{formatAlertTime(selectedAlert.createdAt || selectedAlert.created_at)}</Typography>
                </Grid>
                {selectedAlert.status === 'resolved' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Resolved At</Typography>
                      <Typography variant="body1">{selectedAlert.resolved_at ? formatAlertTime(selectedAlert.resolved_at) : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Resolution Notes</Typography>
                      <Typography variant="body1">{selectedAlert.resolution_notes || 'No notes provided'}</Typography>
                    </Grid>
                  </>
                )}
                {selectedAlert.status === 'active' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Resolution Notes"
                      placeholder="Enter details of how the emergency was resolved..."
                      value={resolveNotes}
                      onChange={(e) => setResolveNotes(e.target.value)}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={actionLoading}>
            Close
          </Button>
          {selectedAlert?.status === 'active' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleResolve}
              disabled={actionLoading}
            >
              Mark as Resolved
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmergencyPage;
